<template>
    <EChart
        :options="chartOptions"
        card
        :header="$t('averageAvailabilityOfProductsByStores')"
        overlay
        :loading="loading"
        :error="request.getError()"
        :tooltip="$t('tooltip')"
    >
        <template #nav>
            <div class="d-flex gap-3">
                <DropdownFilter v-model="currentSortBy" :items="['averageDesc', 'averageAsc', 'changeDesc', 'changeAsc']" :prefix="`${$t('sortBy')}: `" :disabled="loading" />
            </div>
        </template>
    </EChart>
</template>



<script>
    import camelCase from 'camelcase';
    import { calcPercentDiff } from '@/utils/number';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'AverageAvailabilityOfProductsByStores',
        components: {
            EChart,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                currentSortBy: 'averageDesc',
            };
        },
        computed: {
            loading() {
                return this.request.getLoading() || this.requestCompare.getLoading();
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            sortedChartData() {
                if (this.loading) return {};

                const compareMap = new Map();
                // budujemy mapę kampania -> avgAvailability dla porównania
                for (const item of this.chartDataCompare) {
                    compareMap.set(item.campaign, item.avgAvailability);
                }

                // łączymy dane i liczmy diff w jednym przebiegu
                const dataWithDiff = new Array(this.chartData.length);
                for (let i = 0; i < this.chartData.length; i += 1) {
                    const item = this.chartData[i];
                    const compareValue = compareMap.get(item.campaign) || 0;
                    const diff = calcPercentDiff(item.avgAvailability, compareValue);
                    dataWithDiff[i] = { ...item, diff };
                }

                switch (this.currentSortBy) {
                    case 'averageDesc':
                        return dataWithDiff.slice().sort((a, b) => b.avgAvailability - a.avgAvailability);
                    case 'averageAsc':
                        return dataWithDiff.slice().sort((a, b) => a.avgAvailability - b.avgAvailability);
                    case 'changeDesc':
                        return dataWithDiff.slice().sort((a, b) => b.diff - a.diff);
                    case 'changeAsc':
                        return dataWithDiff.slice().sort((a, b) => a.diff - b.diff);
                    default:
                        return dataWithDiff;
                }
            },
            chartOptions() {
                // jeśli brak danych do wyświetlenia, zwracamy pustą serię
                if (!this.sortedChartData.length) {
                    return { series: [] };
                }

                // przygotowujemy tablice: nazwy kampanii, wartości i procentowe zmiany
                const names = this.sortedChartData.map(item => item.campaign);
                const values = this.sortedChartData.map(item => item.avgAvailability);
                const diffs = this.sortedChartData.map(item => item.diff);
                const summary = values.reduce((sum, v) => sum + v, 0);

                return {
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'shadow' },
                    },
                    xAxis: {
                        type: 'value',
                        max: 'dataMax',
                    },
                    yAxis: [
                        // oś po lewej – kampanie
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                        },
                        // oś po prawej – procentowa zmiana
                        {
                            type: 'category',
                            inverse: true,
                            data: names,
                            position: 'right',
                            axisLine: { show: false },
                            axisTick: { show: false },
                            axisLabel: {
                                rich: {
                                    pos: {
                                        color: 'green',
                                        backgroundColor: 'rgba(0,255,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    zero: {
                                        color: '#666',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                    neg: {
                                        color: 'red',
                                        backgroundColor: 'rgba(255,0,0,0.1)',
                                        padding: [2, 4],
                                        borderRadius: 3,
                                    },
                                },
                                formatter: (value, idx) => {
                                    const diff = diffs[idx];
                                    const pct = this.$format.percent(diff);
                                    if (diff > 0) return `{pos|▲${pct}}`;
                                    if (diff < 0) return `{neg|▼${pct}}`;
                                    return `{zero|${pct}}`;
                                },
                            },
                        },
                    ],
                    series: [
                        {
                            type: 'bar',
                            stack: 'total',
                            // etykiety wewnątrz słupków: % udziału + liczba
                            label: {
                                show: true,
                                position: 'insideLeft',
                                formatter: params => {
                                    const value = params.data.value;
                                    return `${this.$format.percent((value * 100) / summary, 2)} (${value})`;
                                },
                            },
                            data: values.map(value => ({ value })),
                        },
                    ],
                };
            },
        },
        methods: {
            pascalCase(text) {
                return camelCase(text, { pascalCase: true });
            },
            computeChartData(source) {
                /* eslint-disable no-restricted-syntax, guard-for-in */
                // Mapujemy od razu delty: campaign -> Map<timestamp, delta>
                const deltasByCampaign = new Map();

                for (const productObj of Object.values(source || {})) {
                    const charts = productObj?.charts || {};
                    for (const chartKey of Object.keys(charts)) {
                        const group = charts[chartKey] || [];
                        for (const chartObj of group) {
                            const campaign = chartObj?.offer?.campaign;
                            if (!campaign) continue; // eslint-disable-line no-continue

                            const segments = chartObj?.chart?.segments || [];
                            if (segments.length === 0) continue; // eslint-disable-line no-continue

                            let mapForCampaign = deltasByCampaign.get(campaign);
                            if (!mapForCampaign) {
                                mapForCampaign = new Map();
                                deltasByCampaign.set(campaign, mapForCampaign);
                            }

                            for (const segment of segments) {
                                const tStart = typeof segment.start === 'number' ? segment.start : Number(segment.start);
                                const tEnd = typeof segment.end === 'number' ? segment.end : Number(segment.end);

                                mapForCampaign.set(tStart, (mapForCampaign.get(tStart) || 0) + 1);
                                mapForCampaign.set(tEnd, (mapForCampaign.get(tEnd) || 0) - 1);
                            }
                        }
                    }
                }

                const campaignSummaries = [];
                for (const [campaign, grouped] of deltasByCampaign.entries()) {
                    // sortujemy unikalne timestampy
                    const times = Array.from(grouped.keys()).sort((a, b) => a - b);
                    if (times.length === 0) continue; // eslint-disable-line no-continue

                    let cumulative = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let prevTime = times[0];

                    // ważona średnia „po czasie” w jednym przebiegu
                    for (let i = 0; i < times.length; i += 1) {
                        const time = times[i];
                        const duration = time - prevTime;
                        if (duration > 0) {
                            weightedSum += cumulative * duration;
                            totalDuration += duration;
                            prevTime = time;
                        }
                        cumulative += grouped.get(time) || 0;
                    }

                    // jeżeli nie mieliśmy „odcinka czasu”, fallback na stan końcowy
                    const avg = totalDuration ? weightedSum / totalDuration : cumulative;

                    campaignSummaries.push({
                        campaign,
                        avgAvailability: Math.round(avg),
                    });
                }

                // sort malejąco po średniej
                campaignSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return campaignSummaries;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
            resetFilters() {
                this.currentSortBy = 'averageDesc';
            },
        },
        created() {
            this.parentComponent.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>


<i18n lang="json" locale="pl" scope="local">
{
    "tooltip": "Wykres prezentuje ranking według średniej dostępności ofert produktów producenta per sklep [% oraz ilościowo] w sklepach wyświetlanych w widgecie (ustawienia domyślnie). Aby sprawdzić liczbę ofert w pozostałych sklepach, użyj filtra Sklep."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "tooltip": "The chart presents the ranking according to the average availability of offers from the manufacturer's products per store [% and quantity] in stores displayed in the widget (settings by default). To check the number of offers in other stores, use the shop filter."
}
</i18n>
